import type { QuestionResponseSchema } from "@/hera-api/client/types.gen";

/**
 * `answer_options` is a map of option label → jump target. PostgreSQL JSONB does
 * not preserve object key order, so the API also returns `answer_option_keys_order`
 * when known. Use that for select ordering and defaults — do not sort A–Z unless
 * appending keys not listed in order using the map's key order (not A–Z).
 */
export type AnswerOptionsDict = QuestionResponseSchema["answer_options"];

/** Persisted operator order from `answer_option_keys_order` on the question. */
export type AnswerOptionKeysOrder = readonly string[] | null | undefined;

export function getAnswerOptionKeysInConfiguredOrder(
  answerOptions: AnswerOptionsDict,
  keysOrder?: AnswerOptionKeysOrder,
): string[] {
  if (!answerOptions || typeof answerOptions !== "object") {
    return [];
  }
  const keys = Object.keys(answerOptions);
  if (!keysOrder?.length) {
    return keys;
  }
  const set = new Set(keys);
  const ordered: string[] = [];
  const seen = new Set<string>();
  for (const k of keysOrder) {
    if (set.has(k) && !seen.has(k)) {
      ordered.push(k);
      seen.add(k);
    }
  }
  // Preserve insertion order from answer_options for keys missing from keysOrder
  // (e.g. new option added server-side). Do not sort A–Z — that breaks operator order.
  const rest = keys.filter((k) => !seen.has(k));
  return [...ordered, ...rest];
}

export function answerOptionsToSelectOptions(
  answerOptions: AnswerOptionsDict,
  keysOrder?: AnswerOptionKeysOrder,
): { value: string; label: string }[] {
  return getAnswerOptionKeysInConfiguredOrder(answerOptions, keysOrder).map(
    (option) => ({
      value: option,
      label: option,
    }),
  );
}

export function getFirstAnswerOptionKey(
  answerOptions: AnswerOptionsDict,
  keysOrder?: AnswerOptionKeysOrder,
): string | undefined {
  const keys = getAnswerOptionKeysInConfiguredOrder(answerOptions, keysOrder);
  return keys[0];
}
